import { useAuthStore } from '../store/authStore';
import { TIER_LIMITS, type Tier, type TierLimits } from '../lib/subscription';
import type { Subscription } from '../types/auth';

interface UseSubscriptionReturn {
  tier: Tier;
  limits: TierLimits;
  isTrialing: boolean;
  trialDaysLeft: number;
  isPastDue: boolean;
  /** true when the user is offline and their cached subscription has expired */
  isOfflineDowngraded: boolean;
  subscription: Subscription | null;
}

export function useSubscription(): UseSubscriptionReturn {
  const subscription = useAuthStore(s => s.subscription);
  const offlineExpired = useAuthStore(s => s.offlineExpired);

  // If subscription cache expired while offline, downgrade to free
  const isOfflineDowngraded = offlineExpired && !!subscription;

  // A trialing row whose trial_end has passed is NOT entitled, even if the
  // server-side expiry job hasn't rewritten the row yet. Compute live here
  // so paywall gating is correct regardless of cron lag.
  const trialExpired =
    subscription?.status === 'trialing'
    && !!subscription.trial_end
    && new Date(subscription.trial_end).getTime() <= Date.now();

  const hasEntitlement = !!subscription && !trialExpired && !isOfflineDowngraded;
  let tier: Tier = hasEntitlement ? (subscription!.tier ?? 'free') : 'free';

  // Dev-only tier override for manual preview testing of paywalled surfaces.
  // Stripped from production bundles by Vite's `import.meta.env.DEV` constant
  // folding. Usage: `?force_tier=academy` on any URL, or
  // `localStorage.setItem('dev-force-tier', 'academy')`. Never trust this
  // anywhere outside the auth/entitlement path — it does not bypass any
  // server-side check, only the local UI gate.
  if (import.meta.env.DEV && typeof window !== 'undefined') {
    const fromQuery = new URLSearchParams(window.location.search).get('force_tier');
    const fromStorage = window.localStorage.getItem('dev-force-tier');
    const forced = (fromQuery || fromStorage) as Tier | null;
    if (forced === 'free' || forced === 'starter' || forced === 'family' || forced === 'academy' || forced === 'launch') {
      tier = forced;
    }
  }

  const limits = TIER_LIMITS[tier];
  const isTrialing = hasEntitlement && subscription?.status === 'trialing';
  const isPastDue = hasEntitlement && subscription?.status === 'past_due';

  let trialDaysLeft = 0;
  if (isTrialing && subscription?.trial_end) {
    const endDate = new Date(subscription.trial_end);
    const now = new Date();
    const diffMs = endDate.getTime() - now.getTime();
    trialDaysLeft = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  return {
    tier,
    limits,
    isTrialing,
    trialDaysLeft,
    isPastDue,
    isOfflineDowngraded,
    subscription,
  };
}
